import { convertISODateTime } from "@utils/time-helpers";
import { getActionBaseOnFeature } from "@utils/actions-helper";
import {
  MODIFY_PAYLOAD,
  INITIAL_PAYLOAD,
  INITIAL_TABS_STATE,
  ENABLE_MODEL,
  RESET_SPECIFIC_TAB,
  DISABLE_ENABLE_TAB,
  SEARCH,
  CLICK_TAB,
  CLICK_SUB_TAB,
  LOAD_PAYLOAD,
  LOAD_FLAT_PAYLOAD,
  TOGGLE_MODAL,
  SET_FILTER_PARAM,
} from "@actions/types";
// CONFIGURATION SET UP
export const setConsoleConfigs = (configs: Record<any, any>) => ({
  type: "SET_CONFIG",
  config: configs,
});

// FILE UPLOAD
export const setShowUploadPanel = (bool: boolean) => ({
  type: "SET_SHOW_UPLOAD_PANEL",
  show: bool,
});

export const setShowDropZone = (bool: boolean) => ({
  type: "SET_SHOW_DROP_ZONE",
  show: bool,
});

export const dropFile = (file: File, base64: string, blob: Blob | null = null) => ({
  type: "DROP_FILE",
  file: file,
  ...(base64 ? { base64: base64 } : {}),
  ...(blob ? { blob: blob } : {}),
});

export const removeFile = () => ({
  type: "REMOVE_FILE",
});

export const storeImageDetails = (imageDetails: Record<string, any>) => ({
  type: "STORE_IMAGE_DETAILS",
  imageDetails: imageDetails,
});

// IMAGE UPLOAD
export const clearSelectedImage = () => ({
  type: "CLEAR_SELECTED_IMAGE",
});

export const restorePosition = () => ({
  type: "RESTORE_POSITION",
});

export const clearPosition = () => ({
  type: "CLEAR_POSITION",
});

export const setActualHeightWidth = (imageDetails: Record<string, any>) => ({
  type: "SET_ACTUAL_HEIGHT_WIDTH",
  imageDetails: imageDetails,
});

export const selectImage = (imageObj: Record<string, any>, placeholdit: string) => ({
  type: "SELECT_IMAGE",
  placeholdit: placeholdit,
  img: imageObj.image,
  fileFormat: imageObj.type,
  fileSize: imageObj.size,
  pixelSize: imageObj,
});

// DISPLAY SPINNER
export const showSpinner = () => ({
  type: "SHOW_SPINNER",
});

export const stopSpinner = () => ({
  type: "HIDE_SPINNER",
});

// SEARCH FILTER COMPONENT AND SHARED TABLE VIEWS
export const setPageNumber = (pageNumber: number, prevPage: number | null = null) => ({
  type: "SET_PAGE_NUMBER",
  pages: {
    page_number: pageNumber,
    ...(prevPage ? { pre_page_number: prevPage } : {}),
  },
});

export const updatePageRoute = (pageRoute: string) => ({
  type: "UPDATE_PAGE_ROUTE",
  pageRoute: pageRoute,
});

export const toggleModal = (modalState: boolean, feature?: string) => ({
  type: getActionBaseOnFeature(TOGGLE_MODAL, feature ?? ""),
  modalState: modalState,
});

export const enableModal = (enableModalSubmit: boolean, feature?: string) => ({
  type: getActionBaseOnFeature(ENABLE_MODEL, feature ?? ""),
  enableModalSubmit: enableModalSubmit,
});

export const clickTab = (selectedTab: string, feature?: string) => ({
  type: getActionBaseOnFeature(CLICK_TAB, feature ?? ""),
  status: selectedTab,
});

export const setTableView = (view: string) => ({
  type: "SET_TABLE_VIEW",
  view: view,
});

export const loadStatusDetail = (id: string, status: string) => ({
  type: "LOAD_STATUS_DETAIL",
  idTarget: id,
  itemStatus: status,
});

export const addMarketingItem = (itemType: string, item: Record<string, any>, status: string = "live") => ({
  type: `ADD_${itemType.toUpperCase()}_ITEM`,
  itemDetails: item,
  status: status,
});

export const updateMarketingItem = (
  itemType: string,
  item: Record<string, any>,
  updateAction: string,
  setSelected: string = "live"
) => ({
  type: `UPDATE_${itemType.toUpperCase()}_ITEM`,
  itemType: itemType,
  itemDetails: item,
  updateAction: updateAction,
  setSelected: setSelected,
});

export const searchTableValues = (searchValue: string) => ({
  type: "SEARCH_TABLE_VALUES",
  searchValue: searchValue,
});

export const clearSearch = (feature: string = "") => ({
  type: getActionBaseOnFeature(SEARCH, feature),
  search: "",
});

export const searchNonTableValues = (searchValue: string, feature: string = "") => ({
  type: getActionBaseOnFeature(SEARCH, feature),
  searchValue: searchValue,
});

export const setPageLimit = (pageLimit: number = 25) => ({
  type: "SET_PAGE_LIMT",
  pageLimit: pageLimit,
});

export const setFilterPlatform = (platformId: string | number | null) => ({
  type: "SET_FILTER_PLATFORM",
  platformId: platformId,
});

export const setFilterDate = (timeSelected: string, dateTime: string) => ({
  type: "SET_FILTER_DATE",
  timeSelected: timeSelected,
  dateTime: dateTime,
  dateDisplay: convertISODateTime(dateTime).toDate,
});

export const setModifiedDate = (timeZone: string) => ({
  type: "SET_MODIFIED_DATE",
  timeZone: timeZone,
});

export const removeFilter = (filter: string) => ({
  type: "REMOVE_FILTER",
  filterToRemove: filter,
});

export const resetFilterButtonClick = () => ({
  type: "RESET_FILTER_ALL",
  platformId: "all",
});

// SHARED DOCK ACTIONS
export const setAlertDock = (dockContent: { title: string; message: string; closeDockCallback?: () => void; constructDiv?: () => void | Element }) => ({
  type: "SET_ALERT_DOCK",
  dockTitle: dockContent.title,
  dockMessage: dockContent.message,
  closeDockCallback: dockContent.closeDockCallback,
  ...(dockContent.constructDiv
    ? { constructDiv: dockContent.constructDiv }
    : {}),
});

export const setConfirmationDock = (dockContent: { title: string; message?: string | React.ReactNode; idTarget?: any; constructDiv?: () => void | Element; updateAction?: string; callback?: () => void }) => ({
  type: "SET_CONFIRMATION_DOCK",
  dockTitle: dockContent.title,
  dockMessage: dockContent.message,
  idTarget: dockContent.idTarget,
  ...(dockContent.constructDiv
    ? { constructDiv: dockContent.constructDiv }
    : {}),
  updateAction: dockContent.updateAction,
  ...(dockContent.callback ? { callback: dockContent.callback } : {}),
});

export const closeDock = () => ({
  type: "CLOSE_DOCK",
});

export const setToastMessage = (message: string, callback?: () => void) => ({
  type: "SET_TOAST_MESSAGE",
  message: message,
  ...(callback ? { callback: callback } : {}),
});

export const reportError = (errorMessage: string) => ({
  type: "SET_ALERT_DOCK",
  dockTitle: "Error",
  dockMessage: errorMessage,
});

// FORM HANDLING
export const clearFormFields = () => ({
  type: "RESET_FORM_FIELDS",
});

export const disableFormFields = (disableFields: boolean) => ({
  type: "DISABLE_FORM_FIELDS",
  disableFields: disableFields,
});

export const validateInputs = (errors: boolean) => ({
  type: "VALIDATE_INPUTS",
  hasErrors: errors,
});

export const setError = (error: string | Record<string, any>) => ({
  type: "SET_ERROR",
  error: error,
});

export const setTabErrors = (validationErrors: Record<string, any>) => ({
  type: "SET_TAB_ERRORS",
  validationErrors: validationErrors,
});

export const enablePost = (errors: boolean) => ({
  type: "ENABLE_POST",
  hasErrors: errors,
});

export const disableSave = () => ({
  type: "DISABLE_SAVE",
});

export const enableSave = () => ({
  type: "ENABLE_SAVE",
});

// GENERIC FORM ACTIONS
export const setTitle = (title: string) => ({
  type: "SET_TITLE",
  title: title,
});

// change in appSettingAction
export const setPlatform = (platform: string) => ({
  type: "SET_PLATFORM",
  platform: platform,
});

export const setName = (name: string) => ({
  type: "SET_NAME",
  name: name,
});

export const setSubname = (subname: string) => ({
  type: "SET_SUBNAME",
  subname: subname,
});

export const setExternalID = (externalID: string) => ({
  type: "SET_EXTERNAL_ID",
  externalID: externalID,
});

export const setDescriptionMessage = (description: string) => ({
  type: "SET_DESCRIPTION",
  description: description,
});

export const setDowntimePay = (message: string) => ({
  type: "SET_DOWNTIME_PAY",
  downtimePay: message,
});

export const setDowntimeTitle = (message: string) => ({
  type: "SET_DOWNTIME_TITLE",
  downtimeTitle: message,
});

export const setDowntimeDescription = (message: string) => ({
  type: "SET_DOWNTIME_DESCRIPTION",
  downtimePayDescription: message,
});

export const setFolderLocation = (folder_location: string) => ({
  type: "SET_FOLDER_LOCATION",
  folder_location: folder_location,
});

export const setAccessibilityLabel = (accessibilityLabel: string) => ({
  type: "SET_ACCESSIBILITY_LABEL",
  accessibilityLabel: accessibilityLabel,
});

export const setDowntimeAccessibilityPay = (accessibilityLabel: string) => ({
  type: "SET_ACCESSIBILITY_PAY",
  downtimePayAccessibility: accessibilityLabel,
});

export const setDowntimeAccessibilityTitle = (accessibilityLabel: string) => ({
  type: "SET_ACCESSIBILITY_TITLE",
  downtimeTitleAccessibility: accessibilityLabel,
});

export const setDowntimeAccessibilityDescription = (accessibilityLabel: string) => ({
  type: "SET_ACCESSIBILITY_DESCRIPTION",
  downtimeDescriptionAccessibility: accessibilityLabel,
});

export const setScheduledClients = (scheduledClients: string[]) => ({
  type: "SET_SCHEDULED_CLIENTS",
  scheduledClients: scheduledClients,
});

export const setSupplementalData = (supplementalData: string | Record<string, any>) => ({
  type: "SET_SUPPLEMENTAL_DATA",
  supplementalData: supplementalData,
});

export const setAddressLineOne = (addressOne: string) => ({
  type: "SET_ADDRESS_LINE_ONE",
  addressOne: addressOne,
});

export const setAddressLineTwo = (addressTwo: string) => ({
  type: "SET_ADDRESS_LINE_TWO",
  addressTwo: addressTwo,
});

export const setCity = (city: string) => ({
  type: "SET_CITY",
  city: city,
});

export const setStateProvince = (stateProvince: string) => ({
  type: "SET_STATE_PROVINCE",
  stateProvince: stateProvince,
});

export const setZipCode = (zipCode: string) => ({
  type: "SET_ZIP_CODE",
  zipCode: zipCode,
});

export const setCountryCode = (countryCode: string) => ({
  type: "SET_COUNTRY_CODE",
  countryCode: countryCode,
});

export const setLongitude = (longitude: string) => ({
  type: "SET_LONGITUDE",
  longitude: longitude,
});

export const setLatitude = (latitude: string) => ({
  type: "SET_LATITUDE",
  latitude: latitude,
});

export const setUrl = (url: string) => ({
  type: "SET_URL",
  url: url,
});

export const setPhoneNumber = (phoneNumber: string) => ({
  type: "SET_PHONE_NUMBER",
  phoneNumber: phoneNumber,
});

export const setFaxNumber = (faxNumber: string) => ({
  type: "SET_FAX_NUMBER",
  faxNumber: faxNumber,
});

export const setNewDate = (whatToSet: string, dateString: string, roundUp: boolean = true) => ({
  type: "SET_NEW_DATE",
  whatToSet: whatToSet,
  dateString: dateString,
  roundUp: roundUp,
});

export const setNewTime = (whatToSet: string, timeString: string, timeZoneOverride?: string) => ({
  type: "SET_NEW_TIME",
  whatToSet: whatToSet,
  timeString: timeString,
  timeZoneOverride: timeZoneOverride,
});

// SEGMENT COMPONENT
export const updateSegments = (segment_id: string) => ({
  type: "SET_USER_SEGMENT",
  segment_id,
});

export const setDeviceSegments = (segments: string[]) => ({
  type: "SET_DEVICE_SEGMENTS",
  segments: segments,
});

//ACTION COMPONENT
export const setActionType = (actionType: string) => ({
  type: "SET_ACTION_TYPE",
  script_type: actionType,
});

export const setActionValue = (actionValue: string) => ({
  type: "SET_ACTION_VALUE",
  script_value: actionValue,
});

export const setPromotionOptions = (promotions: string[]) => ({
  type: "SET_PROMOTION_OPTIONS",
  promotions: promotions,
});

export const setFeaturePermissions = (featurePermissions: Record<string, any>) => ({
  type: "SET_FEATURE_PERMISSIONS",
  featurePermissions: featurePermissions,
});

export const setFileOptions = (files: string[]) => ({
  type: "SET_FILE_OPTIONS",
  files: files,
});

// LOCALIZATION COMPONENT
export const setLocationGroups = (locationGroups: string[]) => ({
  type: "SET_LOCATION_GROUPS",
  locationGroups: locationGroups,
});

export const setStores = (stores: string[]) => ({
  type: "SET_STORES",
  stores: stores,
});

export const updateLocations = (locations: string[]) => ({
  type: "SET_LOCATIONS",
  locations: locations,
});

// Geo Segment Component
export const addLocationSegment = (locations: string[]) => ({
  type: "ADD_LOCATION_SEGMENT",
  locations: locations,
});

export const clearChanges = () => ({
  type: "CLEAR_CHANGES",
});

export const clearErrors = () => ({
  type: "CLEAR_ERRORS",
});

export const clearLog = () => ({
  type: "CLEAR_LOG",
});

export const logChangedKey = (keyChanged: string | string[], toggleSetting: boolean = false) => ({
  type: "LOG_CHANGES",
  keyChanged: keyChanged,
  toggleSetting: toggleSetting,
});

export const recordChangeObject = (changeDict: Record<string, any>) => ({
  type: "LOG_CHANGE_DICT",
  changeDict: changeDict,
});

export const setGeoSegmentName = (name: string) => ({
  type: "SET_GEO_SEGMENT_NAME",
  name: name,
});

export const setGeoSegmentDescription = (description: string) => ({
  type: "SET_GEO_SEGMENT_DESCRIPTION",
  description: description,
});

export const clearGeoSegmentForm = () => ({
  type: "CLEAR_GEO_SEGMENT_FORM",
});

export const setCTIDOptions = (clientList: string[]) => ({
  type: "SET_CTID_OPTIONS",
  clientList: clientList,
});

export const setCommercialCTIDOptions = (clientList: string[]) => ({
  type: "SET_COMMERCIAL_CTID_OPTIONS",
  commercialClients: clientList,
});

export const setUserRole = (userRole: string) => ({
  type: "SET_USER_ROLE",
  userRole: userRole,
});

export const displayBlockerContainer = (block: boolean) => ({
  type: "DISPLAY_BLOCKER",
  displayBlocker: block,
});

export const addBatchCall = (callType: string, timeID: string) => ({
  type: "ADD_BATCH_CALL",
  callType: callType,
  timeID: timeID,
});

export const loadBatchCall = (batchCalls: any[]) => ({
  type: "LOAD_BATCH_CALL",
  batchCalls: batchCalls,
});

export const enableCreateButton = (enableCreate: boolean) => ({
  type: "ENABLE_CREATE",
  enableCreate: enableCreate,
});
export const setFilterByReleaseVersion = (filterByReleaseVersion: boolean) => ({
  type: SET_FILTER_PARAM,
  filterByReleaseVersion: filterByReleaseVersion,
});
export const setSortParams = (sortKey: string, reverse?: boolean) => ({
  type: "SET_SORT_PARAM",
  sortKey: sortKey,
  reverse: reverse ?? false,
});

export const initialTabsState = (tabs: any, feature: string) => ({
  type: getActionBaseOnFeature(INITIAL_TABS_STATE, feature),
  tabs,
});

export const setPayload = (payload: any, feature: string = "") => ({
  type: getActionBaseOnFeature(LOAD_PAYLOAD, feature),
  payload: payload,
});

export const setFlatPayload = (flatPayload: any, feature: string = "") => ({
  type: getActionBaseOnFeature(LOAD_FLAT_PAYLOAD, feature),
  flatPayload: flatPayload,
});

export const setInitialPayload = (initialPayload: any, feature: string = "") => ({
  type: getActionBaseOnFeature(INITIAL_PAYLOAD, feature),
  initialPayload: initialPayload,
});

export const clickSubTab = (selectedTab: any, feature: string = "") => ({
  type: getActionBaseOnFeature(CLICK_SUB_TAB, feature),
  status: selectedTab,
});

export const resetTabs = () => ({
  type: "RESET_TABS",
});

export const setSpecificTab = (
  tab: any,
  keyToDelete: any,
  resetAll: boolean = false,
  feature: string = ""
) => ({
  type: getActionBaseOnFeature(RESET_SPECIFIC_TAB, feature),
  tab: tab,
  keyToDelete: keyToDelete,
  resetAll: resetAll,
});

export const modifyPayload = (
  value: any,
  key: any,
  mainTab: any,
  overrideTab: boolean = false,
  feature: string = "",
  enableSave: boolean = true
) => ({
  type: getActionBaseOnFeature(MODIFY_PAYLOAD, feature),
  value,
  key,
  mainTab,
  overrideTab,
  enableSave,
});

export const disableEnableTab = (status: boolean, inputName: string, feature: string = "") => ({
  type: getActionBaseOnFeature(DISABLE_ENABLE_TAB, feature),
  status: status,
  inputName: inputName,
});

export const setStatus = (status: string) => ({
  type: "SET_STATUS",
  status: status,
});

export const setIsCommercialClient = (status: boolean) => ({
  type: "SET_CTID_AS_COMMERICAL_CLIENT",
  isCommercialClient: status,
});
